import * as React from "react";
import { Link } from "react-router";

import { StringListEditor } from "@/components/enrichment/translations-section";
import { useAreaRepository } from "@/pages/areas/hooks/use-area-repository";
import type { Area } from "@/types/area";

export interface AliasEditorProps {
  area: Area;
  /** Resolves the owner of a refused spelling to a name, when the caller knows the city. */
  areaName?: (id: string) => string | undefined;
  /** Called with the area the server returns after each add or remove. */
  onChange: (area: Area) => void;
}

const conflictOwnerId = (error: unknown): string | undefined => {
  const cause = (error as { cause?: unknown }).cause as
    | { response?: { status?: number; data?: { existingId?: string } } }
    | undefined;
  return cause?.response?.status === 409
    ? cause.response.data?.existingId
    : undefined;
};

/** Aliases act at once, outside the form's Save: each add or remove is its own request. */
export const AliasEditor = ({ area, areaName, onChange }: AliasEditorProps) => {
  const repo = useAreaRepository();
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<{
    message: string;
    owner?: string;
    ownerId?: string;
  }>();

  const run = async (act: () => Promise<Area>) => {
    setBusy(true);
    setError(undefined);
    try {
      onChange(await act());
    } catch (e) {
      const ownerId = conflictOwnerId(e);
      setError({
        message: e instanceof Error ? e.message : "Could not save the alias",
        owner: ownerId ? areaName?.(ownerId) : undefined,
        ownerId,
      });
    } finally {
      setBusy(false);
    }
  };

  const onList = async (next: string[]) => {
    const added = next.find((a) => !area.aliases.includes(a));
    const removed = area.aliases.find((a) => !next.includes(a));
    if (added !== undefined) await run(() => repo.addAlias(area.id, added));
    else if (removed !== undefined) {
      await run(async () => (await repo.removeAlias(area.id, removed)).area);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <StringListEditor
        value={area.aliases}
        onChange={(next) => void onList(next)}
        ariaLabel="alias"
        disabled={busy}
      />
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error.ownerId && (
            <>
              This spelling already belongs to{" "}
              <Link
                to={`/areas/edit/${encodeURIComponent(error.ownerId)}`}
                className="underline"
              >
                {error.owner ?? "another area"}
              </Link>
              .{" "}
            </>
          )}
          {error.message}
        </p>
      )}
    </div>
  );
};
